// DM-9: toJSON/fromJSON. fromJSON validates and returns EITHER a Doc or errors — never partial.
import type { Doc, Entity, SheetLogo, SheetRevision, SheetSetup } from "./entities";
import { validateDoc } from "./document";
import { rebuildLintPotPartsInPlace } from "../pipes/lintPot";
import { addPipeCollarsInPlace, addVerticalFittingEndsInPlace } from "../pipes/api";
import { upgradePumpsInPlace } from "../pipes/pumpRedraw";

export function toJSON(doc: Doc): Doc {
  return structuredClone(doc);
}

/** `repaired`: sentences saying what was fixed on load (shown to the user), when anything was. */
export type LoadResult = { ok: true; doc: Doc; repaired?: string[] } | { ok: false; errors: string[] };

/**
 * Fix the one known kind of damage before validating (2026-10-06). Deleting a GROUP used to
 * remove the group but leave its parts in the file with no place in the z-order (makeDeleteCommand
 * fixed in 3.7.1). Those parts were never drawn after the delete, so the drawing the user last saw
 * did not include them. Parts nothing refers to are removed; a part a dimension is attached to is
 * put back on its layer, so the dimension stays valid. Stale z-order ids are dropped too.
 */
export function repairOrphans(doc: Doc): string[] {
  const notes: string[] = [];
  if (!doc.entities || !doc.zOrder || !Array.isArray(doc.layers) || doc.layers.length === 0) return notes;
  repairGroups(doc, notes);
  // 3.15.2: pipes from before get the collar circles of their floor penetration ends (not damage: no note)
  try {
    addPipeCollarsInPlace(doc);
  } catch {
    /* a pipe that cannot be read keeps its five children */
  }
  // 3.15.6: pump plans get the outlet end; pipe ends joined to the side of a pump get a rubber joint (no note)
  try {
    upgradePumpsInPlace(doc);
  } catch {
    /* a pump that cannot be read keeps its ends */
  }
  // 3.24.0: tees and bends up or down get their vertical end, so a pipe can start from it (no note)
  try {
    addVerticalFittingEndsInPlace(doc);
  } catch {
    /* a fitting that cannot be read keeps its ends */
  }
  const groupChildren = new Set<string>();
  for (const e of Object.values(doc.entities)) if (e.type === "group") e.children.forEach((c) => groupChildren.add(c));
  // z-order entries that point at nothing, or appear twice
  let stale = 0;
  const seen = new Set<string>();
  for (const [layerId, ids] of Object.entries(doc.zOrder)) {
    if (!Array.isArray(ids)) continue;
    doc.zOrder[layerId] = ids.filter((id) => {
      const keep = !!doc.entities[id] && !seen.has(id) && !groupChildren.has(id);
      if (!keep) stale++;
      seen.add(id);
      return keep;
    });
  }
  if (stale > 0) notes.push(`${stale} broken entr${stale === 1 ? "y" : "ies"} in the drawing order removed.`);
  const anchored = new Set<string>();
  for (const e of Object.values(doc.entities)) {
    if (e.type !== "dimension") continue;
    for (const a of [e.a, e.b]) if (a && a.kind === "entity") anchored.add(a.entityId);
  }
  let removed = 0,
    restored = 0;
  for (const e of Object.values(doc.entities)) {
    if (groupChildren.has(e.id) || seen.has(e.id)) continue;
    if (anchored.has(e.id)) {
      const layerId = doc.zOrder[e.layerId] ? e.layerId : doc.layers[0]!.id;
      e.layerId = layerId;
      (doc.zOrder[layerId] ??= []).push(e.id);
      restored++;
    } else {
      delete doc.entities[e.id];
      removed++;
    }
  }
  if (removed > 0) notes.push(`${removed} hidden part${removed === 1 ? "" : "s"} left behind by a deleted group removed.`);
  if (restored > 0) notes.push(`${restored} part${restored === 1 ? "" : "s"} of a deleted group kept, because a dimension is attached.`);
  return notes;
}

/**
 * Groups that list parts that do not exist, or share a part with another group (2026-10-20, DP-PL-001: the parts of a
 * lint pot were grouped a second time with Ctrl+G, then that second group was deleted with its parts; the drawing then
 * failed to open with "INV-2: group … child … missing").
 *   - A part listed by two groups stays in one: a fitting, pump or pipe keeps it before a plain group.
 *   - A lint pot with missing parts gets its parts drawn again from its values.
 *   - Any other group drops the missing parts. A group left with one part or none is dissolved (the part goes back on
 *     the layer, where the group was).
 */
function repairGroups(doc: Doc, notes: string[]): void {
  const groups = Object.values(doc.entities).filter((e): e is Extract<Entity, { type: "group" }> => e.type === "group" && Array.isArray(e.children));
  const special = (g: Extract<Entity, { type: "group" }>) => !!(g.meta && (g.meta.fitting || g.meta.pipe || g.meta.pump || g.meta.ports));
  const owner = new Map<string, string>();
  for (const g of [...groups.filter(special), ...groups.filter((g) => !special(g))]) {
    for (const c of g.children) if (!owner.has(c)) owner.set(c, g.id);
  }
  let shared = 0;
  for (const g of groups) {
    const keep = g.children.filter((c) => !doc.entities[c] || owner.get(c) === g.id);
    shared += g.children.length - keep.length;
    g.children = keep;
  }
  if (shared > 0) notes.push(`${shared} part${shared === 1 ? " was" : "s were"} in two groups; each is now in one group.`);
  let pots = 0,
    dropped = 0,
    dissolved = 0;
  for (const g of groups) {
    const missing = g.children.filter((c) => !doc.entities[c]);
    if (missing.length === 0) continue;
    if ((g.meta?.fitting as { kind?: unknown } | undefined)?.kind === "lintpot" && rebuildLintPotPartsInPlace(doc, g.id)) {
      pots++;
      continue;
    }
    g.children = g.children.filter((c) => doc.entities[c]);
    dropped += missing.length;
  }
  for (const g of groups) {
    if (!doc.entities[g.id] || g.children.length >= 2 || (g.meta?.fitting as { kind?: unknown } | undefined)?.kind === "lintpot") continue;
    const list = doc.zOrder[g.layerId];
    const at = list ? list.indexOf(g.id) : -1;
    if (list && at >= 0) list.splice(at, 1, ...g.children);
    else for (const c of g.children) (doc.zOrder[doc.entities[c]!.layerId] ??= []).push(c);
    delete doc.entities[g.id];
    dissolved++;
  }
  if (pots > 0) notes.push(`${pots} lint pot${pots === 1 ? " had" : "s had"} lost ${pots === 1 ? "its" : "their"} parts; the parts were drawn again from the lint pot values.`);
  if (dropped > 0) notes.push(`${dropped} missing part${dropped === 1 ? " was" : "s were"} taken out of ${dropped === 1 ? "its group" : "their groups"}.`);
  if (dissolved > 0) notes.push(`${dissolved} group${dissolved === 1 ? "" : "s"} with one part or none dissolved.`);
}

/**
 * The print sheet of a drawing, each value checked. A damaged sheet never stops a drawing from opening: the drawing
 * opens without it, and the print box starts again from its defaults.
 */
export function cleanSheet(raw: unknown): SheetSetup | undefined {
  if (raw === null || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  const o = raw as Record<string, unknown>;
  const num = (v: unknown): number => (typeof v === "number" && Number.isFinite(v) ? v : 0);
  if (typeof o.titleBlock !== "string" || typeof o.paper !== "string") return undefined;
  const values: Record<string, string> = {};
  if (o.values && typeof o.values === "object" && !Array.isArray(o.values)) {
    for (const [k, v] of Object.entries(o.values)) if (typeof v === "string") values[k] = v;
  }
  const revisions: SheetRevision[] = Array.isArray(o.revisions)
    ? o.revisions
        .filter((r): r is Record<string, unknown> => r !== null && typeof r === "object")
        .map((r) => ({
          rev: String(r.rev ?? ""),
          date: String(r.date ?? ""),
          description: String(r.description ?? ""),
          drawnBy: String(r.drawnBy ?? ""),
          approvedBy: String(r.approvedBy ?? "")
        }))
    : [];
  const out: SheetSetup = {
    titleBlock: o.titleBlock,
    paper: o.paper,
    landscape: o.landscape !== false,
    marginMm: Math.max(0, Math.min(50, num(o.marginMm))),
    scale: o.scale === "fit" || (typeof o.scale === "number" && o.scale > 0) ? (o.scale as "fit" | number) : "fit",
    turn: o.turn === 90 || o.turn === 180 || o.turn === 270 ? o.turn : 0,
    offsetX: num(o.offsetX),
    offsetY: num(o.offsetY),
    values,
    revisions
  };
  const logoOf = (v: unknown): SheetLogo | null => {
    const l = v as Record<string, unknown> | null | undefined;
    if (!l || (l.mime !== "image/png" && l.mime !== "image/jpeg") || typeof l.base64 !== "string") return null;
    return { mime: l.mime, base64: l.base64, widthPx: num(l.widthPx) || 1, heightPx: num(l.heightPx) || 1 };
  };
  const logo = logoOf(o.logo);
  if (logo) out.logo = logo;
  const clientLogo = logoOf(o.clientLogo);
  if (clientLogo) out.clientLogo = clientLogo;
  return out;
}

const REQUIRED_KEYS: (keyof Doc)[] = [
  "schema",
  "id",
  "name",
  "createdAt",
  "modifiedAt",
  "units",
  "precision",
  "defaultStyle",
  "dimStyle",
  "canvasGrid",
  "layers",
  "entities",
  "zOrder",
  "assets"
];

export function fromJSON(raw: unknown): LoadResult {
  if (raw === null || typeof raw !== "object" || Array.isArray(raw)) {
    return { ok: false, errors: ["serialize: not an object"] };
  }
  const obj = raw as Record<string, unknown>;

  if (typeof obj.schema !== "number") return { ok: false, errors: ["serialize: missing schema"] };
  // EC-DM-9: newer schema than this app understands.
  if (obj.schema > 1) {
    return { ok: false, errors: ["This project was made with a newer version."] };
  }

  const missing = REQUIRED_KEYS.filter((k) => !(k in obj));
  if (missing.length > 0) {
    return { ok: false, errors: missing.map((k) => `serialize: missing field ${k}`) };
  }

  // Drop unknown extra fields with a console warning (DM-9 forward compatibility).
  const known = new Set<string>(REQUIRED_KEYS);
  const clean: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (k === "sheet") continue; // optional, checked below
    if (known.has(k)) clean[k] = v;
    else console.warn(`serialize: dropping unknown field "${k}"`);
  }
  const sheet = cleanSheet(obj.sheet);
  if (sheet) clean.sheet = sheet;

  const doc = structuredClone(clean) as unknown as Doc;
  const repaired = repairOrphans(doc);
  const errors = validateDoc(doc);
  if (errors.length > 0) return { ok: false, errors };

  // Legacy normalization (F2): highlight opacity used to be a hardcoded 0.5 at render
  // time; it now lives in style.opacity. Old highlights carry opacity 1 → restore 0.5.
  for (const e of Object.values(doc.entities)) {
    if (e.type === "marker" && e.kind === "highlight" && e.style.opacity === 1) e.style.opacity = 0.5;
  }
  return repaired.length > 0 ? { ok: true, doc, repaired } : { ok: true, doc };
}
